<template>
  <ProfessionTaskHubPage v-if="taskHubPage" v-bind="taskHubPage" />
  <JoszakiPage v-else :page-name="pageName">
    <div
      v-if="useModularProfessionPage && profession"
      class="flex justify-end"
    >
      <FavouriteButton
        :entity-type="FAVOURITE_ENTITY_TYPES.PROFESSION"
        :entity-id="profession.id"
        button-class="!text-2xl"
      />
    </div>

    <div
      v-if="!useModularProfessionPage"
      class="flex items-center justify-between gap-2"
    >
      <BreadCrumbs :links="getBreadCrumbs" />
      <FavouriteButton
        v-if="profession"
        :entity-type="FAVOURITE_ENTITY_TYPES.PROFESSION"
        :entity-id="profession.id"
        button-class="!text-2xl"
      />
    </div>

    <ModularProfessionPage
      v-if="useModularProfessionPage && profession"
      :profession="profession"
    />

    <div v-else class="grid grid-cols-1 gap-8">
      <ProfessionPageHeading />
      <OnlineCoverageBanner
        v-if="isOnlineProfession"
        class="sticky top-20 z-30"
      />
      <ProfessionPageSectionPeopleSwiper />
      <ProfessionPageSectionCityTabs />
      <ProfessionPageSectionProfessionTask />
      <ProfessionPageSectionAdviceBlock />

      <BlockTender
        text-input
        @requestTender="
          onRequestTender({ source: 'banner-block', description: $event })
        "
      />

      <ProfessionPageSectionLoading class="" :is-loading="loading" />
    </div>
  </JoszakiPage>
</template>

<router>
{
  name: 'professionPage'
}
</router>

<script>
import { computed } from "@nuxtjs/composition-api";
import MAIN_PAGE_SSR from "./mainPageSSR.graphql";
import PROFESSION_PAGE_DATA from "./professionPageData.graphql";
import { redirectHomeOnMissing } from "~/helpers/redirect";
import ProfessionTaskHubPage from "~/components/_refactored/professionTaskPage/ProfessionTaskHubPage.vue";
import { loadProfessionTaskHubPage } from "~/components/_refactored/professionTaskPage/loadProfessionTaskPage";
import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";
import ProfessionPageHeading from "~/components/_refactored/professionPage/ProfessionPageHeading.vue";
import ProfessionPageSectionLoading from "~/components/_refactored/professionPage/ProfessionPageSectionLoading.vue";
import ProfessionPageSectionPeopleSwiper from "~/components/_refactored/professionPage/ProfessionPageSectionPeopleSwiper.vue";
import ProfessionPageSectionCityTabs from "~/components/_refactored/professionPage/ProfessionPageSectionCityTabs.vue";
import ProfessionPageSectionProfessionTask from "~/components/_refactored/professionPage/ProfessionPageSectionProfessionTask.vue";
import ProfessionPageSectionAdviceBlock from "~/components/_refactored/professionPage/ProfessionPageSectionAdviceBlock.vue";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import OnlineCoverageBanner from "~/components/_refactored/blocks/OnlineCoverageBanner.vue";
import ModularProfessionPage from "~/components/_refactored/professionPage/profession-page-modules/ModularProfessionPage.vue";
import { useUserStore } from "~/stores/user";
import { useProfessionNavLinksStore } from "~/stores/professionNavLinks";

import {
  EVENT_REQUEST_TENDER,
  useRequestTender,
} from "~/composables/requestTender";
import { EVENTS } from "~/pages/professionals/_profession/events";
import {
  EVENT_OPEN_PERSON_PROFILE,
  useTrackOpenPersonProfile,
} from "~/composables/tracking/useTrackOpenPersonProfile";
import { usePublicHead } from "~/composables/head/useHead";
import { useFetchProfessionals } from "~/components/_refactored/profession";
import { EVENT_PAGE_NAMES } from "~/data/pageNames";
import { isProfessionHubEmpty } from "~/components/_refactored/professionPage/professionHubIndexing";
import { emptyPageRobotsMeta } from "~/components/_refactored/professionPage/emptyPageRobotsMeta";
import FavouriteButton from "~/components/_refactored/favourite/FavouriteButton.vue";
import { FAVOURITE_ENTITY_TYPES } from "~/composables/favourite/favouriteEntityTypes";
import { isOnlineProfessionEnabled } from "~/experiments/onlineProfession";

const IS_MODULAR_PROFESSION_PAGE_ENABLED =
  process.env.ENABLE_MODULAR_PROFESSION_PAGE === "true";

const MODULAR_PROFESSION_PAGE_USER_IDS = [1188288, 32480, 235006, 571754];

export default {
  name: "ProfessionPage",
  components: {
    BlockTender,
    BreadCrumbs,
    OnlineCoverageBanner,
    ModularProfessionPage,
    ProfessionPageSectionLoading,
    ProfessionPageHeading,
    ProfessionPageSectionPeopleSwiper,
    ProfessionPageSectionCityTabs,
    ProfessionPageSectionProfessionTask,
    ProfessionPageSectionAdviceBlock,
    FavouriteButton,
    ProfessionTaskHubPage,
  },
  provide() {
    return {
      profession: computed(() => this.profession),
      loading: computed(() => this.loading),
      cities: computed(() => this.cities),
      peopleByCity: computed(() => this.peopleByCity),
      peopleByProfessionTask: computed(() => this.peopleByProfessionTask),
      professionTasks: computed(() => this.professionTasks),
      inspirations: computed(() => this.inspirations),
      selectedCity: computed(() => this.selectedCity),
      selectedProfessionTask: computed(() => this.selectedProfessionTask),
      articles: computed(() => this.articles),
    };
  },
  layout: "DefaultLayout",
  setup() {
    const { trackOpenPersonProfile } = useTrackOpenPersonProfile();
    const { requestTender } = useRequestTender();
    return {
      fetchProfessionals: useFetchProfessionals(),
      requestTender,
      trackOpenPersonProfile,
      pageName: EVENT_PAGE_NAMES.PROFESSIONS_PAGE,
      FAVOURITE_ENTITY_TYPES,
    };
  },
  async asyncData(context) {
    const professionSeoName = context.route.params.profession;
    const fetchProfessionals = useFetchProfessionals(context);

    try {
      // TODO add query to auto select city and professionTasks
      const [professionPageData, mainPageContent] = await Promise.all([
        context.$query(PROFESSION_PAGE_DATA, {
          professionSeoName,
          citiesInput: {
            minPopulation: 10000,
            orderBy: "population",
            order: "desc",
          },
        }),
        context.$query(MAIN_PAGE_SSR, {
          professionSeoName,
        }),
      ]);

      if (!mainPageContent.professionBySeoName) {
        const taskHubPage = await loadProfessionTaskHubPage(
          context,
          professionSeoName
        );
        if (taskHubPage?.redirected) {
          return;
        }
        if (taskHubPage) {
          return taskHubPage.data;
        }
        redirectHomeOnMissing(context);
        return;
      }

      const professionTasks =
        professionPageData.popularProfessionTasksByProfession ?? [];
      const selectedProfessionTask = professionTasks?.[0];
      const cities = professionPageData.cities ?? [];
      const selectedCity = cities[0];

      await Promise.all([
        context.store.dispatch(
          "listPage/setProfession",
          mainPageContent.professionBySeoName
        ),
        context.store.dispatch("listPage/setProfessionTask", null),
        useProfessionNavLinksStore(context.$pinia).load(context, {
          professionSeoName,
          professionTasks,
        }),
      ]);

      let peopleByCity = [];
      let peopleByProfessionTask = [];

      if (selectedCity) {
        const queries = [
          fetchProfessionals({
            professionSeoName,
            citySeoName: selectedCity.seoName,
            pageSize: 8,
          }),
        ];

        if (selectedProfessionTask) {
          queries.push(
            fetchProfessionals({
              professionTaskSeoName: selectedProfessionTask.seoName,
              citySeoName: selectedCity.seoName,
              pageSize: 8,
            })
          );
        }

        const results = await Promise.all(queries);
        peopleByCity = results[0]?.people ?? [];
        if (selectedProfessionTask) {
          peopleByProfessionTask = results[1]?.people ?? [];
        }
      }

      return {
        professionSeoName,
        profession: mainPageContent.professionBySeoName,
        professionTasks,
        selectedProfessionTask,
        peopleByProfessionTask,
        peopleByCity,
        cities,
        selectedCity,
        isEmptyHub: isProfessionHubEmpty({
          peopleByCity,
          peopleByProfessionTask,
        }),
        inspirations: mainPageContent.inspirations.items,
        articles: mainPageContent.articles.items,
      };
    } catch (err) {
      console.error("failed to load profession page", err);
      context.$sentry.captureException(JSON.stringify(err), {
        tags: {
          profession_page: "error",
        },
      });
      context.error({
        statusCode: 500,
        message: context.i18n.t("error.codes.INTERNAL_SERVER_ERROR"),
      });
    }
  },
  data() {
    return {
      loading: false,
      taskHubPage: null,
    };
  },
  head() {
    if (this.taskHubPage) {
      return {};
    }
    const title = this.$t("professionPage.title", {
      professionName: this.$helpers.capitalize(this.profession.name),
    });
    const description = this.$i18n.t("professionPage.description", {
      profession: this.$helpers.capitalize(this.profession.name),
    });
    // TODO nuxt3 move to setup
    return {
      ...usePublicHead(
        {
          title,
          description,
          context: this,
        },
        emptyPageRobotsMeta({ isEmpty: this.isEmptyHub })
      ),
      link: [
        {
          rel: "canonical",
          href: `${this.$helpers.topDomain}${this.$route.path}`,
        },
      ],
    };
  },
  computed: {
    useModularProfessionPage() {
      if (IS_MODULAR_PROFESSION_PAGE_ENABLED) return true;
      const userStore = useUserStore(this.$pinia);
      const userId = parseInt(userStore.me?.id);
      return MODULAR_PROFESSION_PAGE_USER_IDS.includes(userId);
    },
    isOnlineProfession() {
      return isOnlineProfessionEnabled(this.$ab) && !!this.profession?.online;
    },
    getBreadCrumbs() {
      const links = [];
      if (this.profession?.name) {
        if (this.profession.professionGroup) {
          const professionsPagePath = this.localePath({
            name: "professionsLandingPage",
            hash: `#${this.profession.professionGroup.seoName}`,
          });
          links.push({
            name: this.profession.professionGroup.name,
            page: professionsPagePath,
          });
        }
        links.push({
          name: this.profession.name,
          link: "",
        });
      }
      return links;
    },
    firstFewProfessionTasks() {
      return (
        this.professionTasks?.slice(
          0,
          Math.min(this.professionTasks.length, 5)
        ) ?? []
      );
    },
    firstFewCitiesWithPersonCount() {
      return this.cities?.slice(0, Math.min(this.cities.length, 7)) ?? [];
    },
    peopleIds() {
      return this.peopleByCity?.map((p) => p.id) ?? [];
    },
  },
  mounted() {
    if (this.taskHubPage) {
      return;
    }
    this.$mitt.on(EVENT_REQUEST_TENDER, this.onRequestTender);
    this.$mitt.on(EVENTS.PROFESSIONS_PAGE_CHANGE_CITY, this.changeCity);
    this.$mitt.on(
      EVENTS.PROFESSIONS_PAGE_CHANGE_PROFESSION_TASK,
      this.changeProfessionTask
    );
    this.$mitt.on(EVENT_OPEN_PERSON_PROFILE, this.onClickPersonCard);
    this.seoTrackViewPage();
    this.$trackers.pageview({
      pagename: this.$trackers.pageNames.PROFESSIONS_PAGE,
      seoName: this.$route.params.profession,
    });
  },
  beforeDestroy() {
    if (this.taskHubPage) {
      return;
    }
    this.$mitt.off(EVENT_REQUEST_TENDER, this.onRequestTender);
    this.$mitt.off(EVENTS.PROFESSIONS_PAGE_CHANGE_CITY, this.changeCity);
    this.$mitt.off(
      EVENTS.PROFESSIONS_PAGE_CHANGE_PROFESSION_TASK,
      this.changeProfessionTask
    );
    this.$mitt.off(EVENT_OPEN_PERSON_PROFILE, this.onClickPersonCard);
    this.$trackers.pageleave({
      pagename: this.$trackers.pageNames.PROFESSIONS_PAGE,
      seoName: this.$route.params.profession,
    });
  },
  methods: {
    changeCity(city) {
      if (city) {
        this.selectedCity = city;
        this.refetchPeople();
      } else {
        this.selectedCity = this.firstFewCitiesWithPersonCount[0];
        this.refetchPeople();
      }
    },
    changeProfessionTask(professionTask) {
      if (professionTask) {
        this.selectedProfessionTask = professionTask;
        this.refetchPeople();
      } else {
        this.selectedProfessionTask = this.firstFewProfessionTasks[0];
        this.refetchPeople();
      }
    },
    async refetchPeople() {
      this.isLoading = true;
      this.peopleByCity =
        (
          await this.fetchProfessionals({
            professionSeoName: this.profession.seoName,
            citySeoName: this.selectedCity.seoName,
            pageSize: 8,
          })
        )?.people ?? [];

      if (this.selectedProfessionTask) {
        this.peopleByProfessionTask =
          (
            await this.fetchProfessionals({
              professionTaskSeoName: this.selectedProfessionTask.seoName,
              citySeoName: this.selectedCity.seoName,
              pageSize: 8,
            })
          )?.people ?? [];
      }
      this.isLoading = false;
    },
    onRequestTender({ person, source, description } = {}) {
      this.requestTender({
        person,
        city: this.city,
        profession: this.profession,
        source,
        description,
      });
    },
    onClickPersonCard(person) {
      this.trackOpenPersonProfile({
        person,
        city: this.selectedCity,
        profession: this.profession,
        professionTask: this.selectedProfessionTask,
        personIndex: this.peopleIds.indexOf(person.id),
      });
      const basePath = this.localePath({
        name: "professionalPage",
        params: { professionalSeoName: person.seoName },
      });
      const hashParams = [];
      if (this.selectedCity?.seoName) {
        hashParams.push(`city=${this.selectedCity.seoName}`);
      }
      if (this.profession?.seoName) {
        hashParams.push(`profession=${this.profession.seoName}`);
      }
      const path =
        hashParams.length > 0
          ? `${basePath}#${hashParams.join("&")}`
          : basePath;
      window.open(path);
    },
    seoTrackViewPage() {
      if (!this.profession) {
        return;
      }
      this.$track.customEvent({
        event: "professionListPage",
        profession: this.profession.seoName, // szakma
      });
    },
  },
};
</script>
